import { normalizeDrill, type Drill } from "../domain/drill";
import { getCoachSession } from "./coach-auth";
import { getWorkspaceKey } from "./coach-workspace-key";
import { loadDrills, replaceDrills } from "./drill-storage";

const API = "/api/coach-drills";

export type CloudSyncResult = {
  drills: Drill[];
  synced: boolean;
};

export { getWorkspaceKey, setWorkspaceKey } from "./coach-workspace-key";

export async function cloudWorkspaceHeaders() {
  const session = await getCoachSession();
  return {
    "Content-Type": "application/json",
    "X-Coach-Workspace-Key": getWorkspaceKey(),
    ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}),
  };
}

export async function syncDrills(): Promise<CloudSyncResult> {
  const local = loadDrills();
  try {
    const requestHeaders = await cloudWorkspaceHeaders();
    const response = await fetch(API, { headers: requestHeaders });
    if (!response.ok) throw new Error(`Cloud load returned ${response.status}`);
    const body = (await response.json()) as { drills?: Drill[] };
    const remote = Array.isArray(body.drills)
      ? body.drills.map(normalizeDrill)
      : [];
    const merged = new Map<string, Drill>();
    for (const drill of [...remote, ...local]) {
      const existing = merged.get(drill.id);
      if (!existing || drill.updatedAt > existing.updatedAt)
        merged.set(drill.id, drill);
    }
    const drills = replaceDrills([...merged.values()]);
    if (drills.length)
      await fetch(API, {
        method: "POST",
        headers: requestHeaders,
        body: JSON.stringify({ drills }),
      });
    return { drills, synced: true };
  } catch {
    return { drills: local, synced: false };
  }
}

export async function saveCloudDrill(drill: Drill) {
  try {
    const response = await fetch(API, {
      method: "POST",
      headers: await cloudWorkspaceHeaders(),
      body: JSON.stringify({ drills: [drill] }),
    });
    return response.ok;
  } catch {
    return false;
  }
}

export async function deleteCloudDrill(id: string) {
  try {
    const response = await fetch(`${API}?id=${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: await cloudWorkspaceHeaders(),
    });
    return response.ok;
  } catch {
    return false;
  }
}
